import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, MapPin, Filter } from "lucide-react";
import api from "@/lib/api";

const CATEGORIES = ["All", "Convention", "Anniversary", "Community"];

// Recurring service programmes excluded from "Upcoming Events"
const SERVICE_CATEGORIES = new Set(["Service", "Devotional", "Prayer"]);

export default function Events() {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const visible = events.filter((e) => !SERVICE_CATEGORIES.has(e.category));
  const filtered = filter === "All" ? visible : visible.filter((e) => e.category === filter);

  return (
    <div data-testid="events-page" className="bg-[#050505] text-white">
      <section className="py-32 md:py-40 px-6 md:px-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-4">Upcoming</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl uppercase font-bold tracking-tighter leading-[0.9]">
            Events & <span className="text-[#ff5c00]">Gatherings</span>
          </h1>
          <p className="text-zinc-300 text-lg md:text-xl max-w-3xl mt-8">
            From Sunday Service to Holy Ghost prayer nights — there's always
            something to plug into. Pick one. Show up. Watch God move.
          </p>
        </div>
      </section>

      <section className="py-12 px-6 md:px-12 border-b border-white/10 bg-[#080808]">
        <div className="max-w-7xl mx-auto flex items-center gap-3 flex-wrap">
          <Filter className="w-4 h-4 text-[#ff5c00]" />
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`filter-${c.toLowerCase()}`}
              className={`font-mono-accent text-xs tracking-[0.2em] uppercase px-4 py-2 rounded-sm border transition-colors ${
                filter === c
                  ? "border-[#ff5c00] text-white bg-[#ff5c00]/10"
                  : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="py-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {filtered.length === 0 ? (
            <p data-testid="no-events" className="text-zinc-500 text-center py-20">No events yet — check back soon.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((ev, i) => (
                <motion.article
                  key={ev.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  data-testid={`event-${i}`}
                  className="group bg-[#0f0f14] border border-white/10 rounded-sm overflow-hidden hover:border-[#ff5c00]/60 hover:-translate-y-1 transition-all"
                >
                <div className="aspect-[16/10] overflow-hidden bg-[#0a0a0d]">
                    {ev.image_url && (
                      <img
                        src={ev.image_url}
                        alt={ev.title}
                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                      />
                    )}
                  </div>
                  <div className="p-6 space-y-3">
                    <span className="inline-block font-mono-accent text-[10px] tracking-[0.25em] uppercase text-[#ff5c00] bg-[#ff5c00]/10 px-2 py-1 rounded-sm">
                      {ev.category}
                    </span>
                    <h3 className="font-display text-2xl uppercase tracking-tight leading-tight">{ev.title}</h3>
                    <p className="text-zinc-400 text-sm line-clamp-3">{ev.description}</p>
                    <div className="pt-3 border-t border-white/10 space-y-1.5 text-sm text-zinc-300">
                      <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#ff5c00]" /> {ev.date}</div>
                      <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#ff5c00]" /> {ev.time}</div>
                      <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#ff5c00]" /> {ev.location}</div>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
